import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useToast } from '../../../../hooks/useToast';
import { waitForJobCompletion } from '../../../../services/asyncJobApi';
import {
  generateScenarioVoiceLines,
  getScenario,
  retakeSceneVoiceLine,
  scenarioVoiceMapQueryKey,
  voiceLinesSplitQueryKey,
  type VoiceLinesJobResult,
} from '../../../../features/scenarios/api';
import { resolveErrorMessage } from '../../../../utils/getErrorMessage';
import { useGenerationState } from '../../state/useGenerationState';
import { friendlyJobErrorMessage } from '../utils';
import type { Scenario, ScenarioResponse } from '../types';
import { motionPlanQueryKey } from './useMotionPlan';
import {
  classifyVoiceLinesError,
  forgetVoiceJob,
  mergeVoiceRefreshedScenes,
  recallVoiceJob,
  rememberVoiceJob,
  summarizeVoiceLinesResult,
  usesVoiceLines,
  type VoiceLinesErrorBody,
} from '../voiceLines';

/** 実行中の声のジョブ (シナリオ id → 種類と対象シーン)。sceneIds が空なら全シーン。 */
export interface VoiceLinesRunning {
  jobId: string;
  kind: 'generate' | 'retake';
  sceneIds: string[];
  /** 撮り直し中の行 */
  lineId?: string;
}

/** 直近の声のジョブの結果のうち、画面に残す注意 (失敗したシーンの一覧・計画の advice)。 */
export interface VoiceLinesLastResult {
  failedLines: string[];
  advice: string | null;
}

/** 「ナレーションを生成」の追加指定。sceneLanguages はシーン id → 発音言語 (無いシーンは ja)。 */
export interface GenerateVoiceLinesOptions {
  sceneLanguages?: Record<string, 'ja' | 'en'>;
}

/** 受付の結果。unassigned は割り当てを促す話者 (speaker_id)。 */
export type VoiceLinesRequestResult =
  | { ok: true; jobId: string }
  | { ok: false; message?: string; unassignedSpeakers?: string[]; linesChanged?: boolean };

/**
 * 行ベースのナレーション (歌モノ以外のすべてのシナリオ) の「ナレーションを生成」「この行だけ撮り直す」の受付とジョブの追跡
 * (docs/features/brand-cut-motion-graphics-plan.md §10.7.3・2g-4)。
 *
 * - 受付後は 1 件のジョブとして生成キュー (GenerationQueueWindow・type='voice') に載せ、backend の段階 (stage) を出す
 * - 完了したらシナリオを取り直して声で変わる列 (声の素材・尺・台本) を画面に写し、声の対応表・計画・同期の鮮度・分割前音声を取り直す
 * - 受付時にジョブ id を sessionStorage に控え、再読み込み後もシナリオが開かれたら追跡を戻す
 */
export function useVoiceLineHandlers({
  scenarios,
  setLastResult,
  openPurchaseModal,
  refetchCredits,
}: {
  scenarios: readonly Scenario[];
  setLastResult: React.Dispatch<React.SetStateAction<ScenarioResponse | null>>;
  openPurchaseModal: () => void;
  refetchCredits: () => unknown;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { setActiveJobs } = useGenerationState();
  const [voiceLinesRunning, setVoiceLinesRunning] = useState<Record<string, VoiceLinesRunning>>({});
  const [voiceLinesLastResult, setVoiceLinesLastResult] = useState<Record<string, VoiceLinesLastResult>>({});
  const trackedRef = useRef<Set<string>>(new Set());
  const scenariosRef = useRef(scenarios);
  scenariosRef.current = scenarios;

  const refreshScenario = useCallback(async (scenarioId: string) => {
    try {
      const updated = await getScenario(scenarioId);
      const rows = (updated.scenes || []).map((sc) => ({ ...sc, id: sc.id as string }));
      setLastResult((prev) => (prev ? {
        ...prev,
        scenarios: prev.scenarios.map((s) => (s.id !== scenarioId ? s : {
          ...s,
          generation_metadata: (updated.generation_metadata as Record<string, unknown> | undefined) ?? s.generation_metadata,
          scenes: mergeVoiceRefreshedScenes(s.scenes, rows),
        })),
      } : prev));
    } catch (err) {
      console.error('声の生成後のシナリオの取り直しに失敗しました', err);
    }
  }, [setLastResult]);

  const trackVoiceJob = useCallback((scenarioId: string, running: VoiceLinesRunning) => {
    const { jobId, kind } = running;
    if (trackedRef.current.has(jobId)) return;
    trackedRef.current.add(jobId);
    rememberVoiceJob(scenarioId, { jobId, kind, sceneIds: running.sceneIds });
    setVoiceLinesRunning((prev) => ({ ...prev, [scenarioId]: running }));
    setVoiceLinesLastResult((prev) => {
      if (!(scenarioId in prev)) return prev;
      const next = { ...prev };
      delete next[scenarioId];
      return next;
    });
    const index = scenariosRef.current.findIndex((s) => s.id === scenarioId) + 1;
    const label = `シナリオ${index > 0 ? index : ''} ${kind === 'retake' ? 'ナレーションの撮り直し' : 'ナレーション'}`;
    setActiveJobs((prev) => [...prev, {
      jobId, status: 'polling' as const, waiting: false, startedAt: Date.now(), type: 'voice' as const, label, scenarioId,
    }]);
    const finish = () => {
      trackedRef.current.delete(jobId);
      forgetVoiceJob(scenarioId, jobId);
      setVoiceLinesRunning((prev) => {
        if (prev[scenarioId]?.jobId !== jobId) return prev;
        const next = { ...prev };
        delete next[scenarioId];
        return next;
      });
      refetchCredits();
      void queryClient.invalidateQueries({ queryKey: scenarioVoiceMapQueryKey(scenarioId) });
      void queryClient.invalidateQueries({ queryKey: motionPlanQueryKey(scenarioId) });
      // 分割位置の調整 (ハサミ) は分割前音声の有無で出し分けるため、生成前に取った空の結果を残さない
      void queryClient.invalidateQueries({ queryKey: voiceLinesSplitQueryKey(scenarioId) });
      void refreshScenario(scenarioId);
    };
    waitForJobCompletion<VoiceLinesJobResult>(jobId, {
      skipVisibilityCheck: true,
      onProgress: (job) => {
        setActiveJobs((prev) => prev.map((j) => (j.jobId === jobId
          ? { ...j, waiting: job.status === 'pending', stage: job.status === 'processing' ? job.stage?.text : undefined }
          : j)));
      },
    })
      .then((job) => {
        const summary = summarizeVoiceLinesResult(job.result_data, kind);
        // ジョブは全シーンが失敗しても completed で終わる (backend)。成功 0 件なら生成キューでも失敗として出す
        setActiveJobs((prev) => prev.map((j) => (j.jobId === jobId
          ? (summary.allFailed
            ? { ...j, status: 'failed' as const, waiting: false, error: summary.failedLines.join(' / ') }
            : { ...j, status: 'completed' as const, waiting: false })
          : j)));
        setVoiceLinesLastResult((prev) => ({ ...prev, [scenarioId]: { failedLines: summary.failedLines, advice: summary.advice } }));
        if (summary.failedLines.length > 0) {
          toast.error(`${summary.message}\n${summary.failedLines.join('\n')}`);
        } else {
          toast.success(summary.message);
        }
        if (summary.advice) toast.show(`ナレーションがシーンの尺に収まりません: ${summary.advice}`);
        finish();
      })
      .catch((error) => {
        const errorMsg = error instanceof Error ? error.message : String(error);
        setActiveJobs((prev) => prev.map((j) => (j.jobId === jobId ? { ...j, status: 'failed' as const, error: errorMsg } : j)));
        toast.error(`${kind === 'retake' ? '行の撮り直し' : 'ナレーションの生成'}に失敗しました: ${friendlyJobErrorMessage(error) ?? errorMsg}`);
        finish();
      });
  }, [queryClient, refetchCredits, refreshScenario, setActiveJobs, toast]);

  // 再読み込み後: 控えてあるジョブがあれば追跡を戻す (行ベースのナレーションのシナリオだけ = 歌モノ以外)
  useEffect(() => {
    for (const s of scenarios) {
      if (!s.id || !usesVoiceLines(s)) continue;
      const remembered = recallVoiceJob(s.id);
      if (!remembered || trackedRef.current.has(remembered.jobId)) continue;
      trackVoiceJob(s.id, { jobId: remembered.jobId, kind: remembered.kind, sceneIds: remembered.sceneIds });
    }
  }, [scenarios, trackVoiceJob]);

  const handleRequestError = useCallback((error: unknown, scenarioId: string, fallback: string, running: Omit<VoiceLinesRunning, 'jobId'>): VoiceLinesRequestResult => {
    const resp = (error as { response?: { status?: number; data?: VoiceLinesErrorBody } }).response;
    const action = classifyVoiceLinesError(resp?.status, resp?.data, resp ? fallback : resolveErrorMessage(error));
    switch (action.kind) {
      case 'purchase':
        openPurchaseModal();
        return { ok: false, message: action.message };
      case 'unassigned':
        toast.error(action.message);
        return { ok: false, message: action.message, unassignedSpeakers: action.speakers };
      case 'lines_changed':
        toast.error(action.message);
        return { ok: false, message: action.message, linesChanged: true };
      case 'running':
        if (action.jobId) {
          toast.show('実行中のナレーションのジョブを追跡します');
          trackVoiceJob(scenarioId, { ...running, jobId: action.jobId });
          return { ok: true, jobId: action.jobId };
        }
        toast.error(action.message);
        return { ok: false, message: action.message };
      case 'mixed':
      case 'message':
      default:
        toast.error(action.message);
        return { ok: false, message: action.message };
    }
  }, [openPurchaseModal, toast, trackVoiceJob]);

  /** 「ナレーションを生成」(sceneIds 省略で全シーン)。options.sceneLanguages はシーンごとの英語発音。 */
  const handleGenerateVoiceLines = useCallback(async (
    scenario: Scenario,
    sceneIds?: string[],
    options?: GenerateVoiceLinesOptions,
  ): Promise<VoiceLinesRequestResult> => {
    const running = { kind: 'generate' as const, sceneIds: sceneIds ?? [] };
    const sceneLanguages = options?.sceneLanguages && Object.keys(options.sceneLanguages).length > 0 ? options.sceneLanguages : undefined;
    try {
      const res = await generateScenarioVoiceLines(scenario.id, {
        ...(sceneIds ? { scene_ids: sceneIds } : {}),
        ...(sceneLanguages ? { scene_languages: sceneLanguages } : {}),
      });
      trackVoiceJob(scenario.id, { ...running, jobId: res.job_id });
      refetchCredits();
      return { ok: true, jobId: res.job_id };
    } catch (error) {
      return handleRequestError(error, scenario.id, 'ナレーションの生成を受け付けられませんでした', running);
    }
  }, [handleRequestError, refetchCredits, trackVoiceJob]);

  /** 「この行だけ撮り直す」。409 VOICE_LINES_CHANGED はシーン全体の生成へ誘導する。 */
  const handleRetakeVoiceLine = useCallback(async (scenario: Scenario, sceneId: string, lineId: string): Promise<VoiceLinesRequestResult> => {
    const running = { kind: 'retake' as const, sceneIds: [sceneId], lineId };
    try {
      const res = await retakeSceneVoiceLine(scenario.id, sceneId, { line_id: lineId });
      trackVoiceJob(scenario.id, { ...running, jobId: res.job_id });
      refetchCredits();
      return { ok: true, jobId: res.job_id };
    } catch (error) {
      return handleRequestError(error, scenario.id, '行の撮り直しを受け付けられませんでした', running);
    }
  }, [handleRequestError, refetchCredits, trackVoiceJob]);

  return { handleGenerateVoiceLines, handleRetakeVoiceLine, refreshVoiceScenario: refreshScenario, voiceLinesRunning, voiceLinesLastResult };
}
